'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import * as React from 'react';

import { FOCUS_STYLES } from '@ui/lib/focus-styles';
import { cn } from '@ui/lib/utils';
import { VisuallyHidden } from './visually-hidden';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/40 backdrop-blur-md hover:backdrop-blur-none transition-all duration-300 ease-in data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 group',
      className
    )}
    {...props}
  >
    <div className="absolute inset-0 flex items-center justify-center">
      <button
        className={cn(
          'flex items-center gap-2 rounded-lg bg-white/10 px-4 py-2.5 shadow-lg ring-1 ring-white/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200 ease-in hover:ring-white/40 hover:shadow-xl',
          FOCUS_STYLES.overlay
        )}
      >
        <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-white text-black ring-1 ring-white/40">
          <X className="h-3.5 w-3.5" />
        </div>
        <span className="text-sm font-bold text-white drop-shadow-sm">
          Click to Close
        </span>
      </button>
    </div>
  </DialogPrimitive.Overlay>
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

// Forward reference for DialogTitle to use in other components
const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-bold leading-none tracking-tight', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => {
  // Check for any DialogTitle in children
  const hasDialogTitle = React.Children.toArray(children).some(
    (child) => React.isValidElement(child) && child.type === DialogTitle
  );

  // Check for any DialogDescription in children
  const hasDialogDescription = React.Children.toArray(children).some(
    (child) => React.isValidElement(child) && child.type === DialogDescription
  );

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg shadow-slate-900/20 duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-lg',
          className
        )}
        {...props}
      >
        {/* Add a default title if one isn't provided */}
        {!hasDialogTitle && (
          <VisuallyHidden asChild>
            <DialogTitle className="text-base font-bold text-slate-800 text-align-left">
              Dialog
            </DialogTitle>
          </VisuallyHidden>
        )}

        {/* Add a default description if one isn't provided */}
        {!hasDialogDescription && (
          <VisuallyHidden asChild>
            <DialogDescription>Dialog content</DialogDescription>
          </VisuallyHidden>
        )}

        {children}
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-lg opacity-70 ring-offset-background hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground inline-flex items-center gap-x-1.5 bg-white px-1.5 py-1.5 text-xs font-bold text-slate-800 shadow-sm  hover:bg-slate-50/80 transition duration-200 ease-in-out">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-1.5 text-left sm:text-left',
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-start sm:space-x-2',
      // When viewport width < 560px, stack buttons vertically in reverse order
      'max-[559px]:flex-col-reverse [&>*]:max-[559px]:w-full max-[559px]:gap-2',
      // Make buttons with data-primary="true" flex-grow, while others stay normal width
      // Add min-width and text overflow handling for primary buttons
      '[&>*[data-primary="true"]]:flex-grow [&>*[data-primary="true"]]:min-w-[180px] [&>*[data-primary="true"]]:whitespace-normal',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
